import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { Link } from 'react-router-dom';
import { API } from '../contexts/AuthContext';
import Tooltip from '../components/Tooltip';
import CalendarioDemandas from '../components/demandas/CalendarioDemandas';
import AcoesDemanda from '../components/demandas/AcoesDemanda';
import { ROXO, TIPOS, TIPO, COLUNAS, ORDEM_STATUS, hoje, fmtPeriodo, situacaoPrazo } from '../components/demandas/demandasUtil';

// Demandas do Marketing — kanban que substitui o quadro do Notion + calendário do planejamento anual.
// Criar é uma linha só (título + prazo opcional); o resto se completa depois, se quiser.
// Demanda 'planejada' fica só no calendário até alguém puxar pro quadro.

const lerAba = () => { try { return localStorage.getItem('demandas_aba') || 'quadro'; } catch { return 'quadro'; } };

const VAZIA = { titulo: '', descricao: '', tipo: '', status: 'a_fazer', prazo: '', data_inicio: '', data_fim: '', campanha_id: '', solicitante: '', link: '', filiais: [], acoes: [] };

export default function Demandas() {
  const [demandas, setDemandas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filiais, setFiliais] = useState([]);
  const [campanhas, setCampanhas] = useState([]);

  const [busca, setBusca] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroLoja, setFiltroLoja] = useState('');
  const [todasFeitas, setTodasFeitas] = useState(false);
  const [aba, setAbaState] = useState(lerAba);
  const setAba = (v) => { setAbaState(v); try { localStorage.setItem('demandas_aba', v); } catch { /* sem storage */ } };

  const [rapida, setRapida] = useState({ titulo: '', prazo: '' });
  const [criando, setCriando] = useState(false);
  const [edit, setEdit] = useState(null); // demanda aberta no modal
  const [salvando, setSalvando] = useState(false);
  const [arrastando, setArrastando] = useState(null);
  const [sobreColuna, setSobreColuna] = useState(null);

  const carregar = useCallback(() => {
    // o calendário mostra o ano todo, inclusive as feitas
    return axios.get(`${API}/demandas`, { params: { feitas_dias: todasFeitas || aba === 'calendario' ? 0 : 30 } })
      .then(r => { setDemandas(r.data); return r.data; })
      .catch(e => { Swal.fire('Erro', e.response?.data?.error || 'Não foi possível carregar as demandas', 'error'); return null; })
      .finally(() => setLoading(false));
  }, [todasFeitas, aba]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    axios.get(`${API}/filiais`).then(r => setFiliais(r.data)).catch(() => {});
    axios.get(`${API}/campanhas`).then(r => setCampanhas(r.data)).catch(() => {});
  }, []);

  const nomeFilial = (c) => filiais.find(f => String(f.codigo) === String(c))?.nome || `Filial ${c}`;

  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return demandas.filter(d =>
      (!termo || `${d.titulo} ${d.descricao || ''} ${d.solicitante || ''} ${d.campanha_nome || ''}`.toLowerCase().includes(termo)) &&
      (!filtroTipo || d.tipo === filtroTipo) &&
      (!filtroLoja || d.filiais.includes(parseInt(filtroLoja)))
    );
  }, [demandas, busca, filtroTipo, filtroLoja]);

  const porColuna = useMemo(() => {
    const m = { a_fazer: [], fazendo: [], feito: [] };
    visiveis.forEach(d => {
      if (d.status === 'planejada') return; // planejada só aparece no calendário
      (m[d.status === 'cancelada' ? 'feito' : d.status] || m.a_fazer).push(d);
    });
    // Feito: mais recente primeiro
    m.feito.sort((a, b) => String(b.concluido_em || b.atualizado_em || '').localeCompare(String(a.concluido_em || a.atualizado_em || '')));
    return m;
  }, [visiveis]);

  // mesmo recorte do quadro (filtros aplicados), igual ao "em aberto"
  const atrasadas = visiveis.filter(d => d.status !== 'planejada' && situacaoPrazo(d)?.label.startsWith('Atrasada')).length;
  const planejadas = visiveis.filter(d => d.status === 'planejada').length;

  async function criarRapida(e) {
    e?.preventDefault();
    if (!rapida.titulo.trim()) return;
    setCriando(true);
    try {
      await axios.post(`${API}/demandas`, { titulo: rapida.titulo, prazo: rapida.prazo || null });
      setRapida({ titulo: '', prazo: '' });
      await carregar();
    } catch (err) {
      Swal.fire('Erro', err.response?.data?.error || 'Não foi possível criar a demanda', 'error');
    } finally { setCriando(false); }
  }

  async function mover(d, status) {
    if (d.status === status) return;
    // otimista: move na tela e desfaz se falhar
    setDemandas(prev => prev.map(x => x.id === d.id ? { ...x, status, concluido_em: status === 'feito' ? new Date().toISOString() : null } : x));
    try {
      await axios.patch(`${API}/demandas/${d.id}/status`, { status });
    } catch (err) {
      Swal.fire('Erro', err.response?.data?.error || 'Não foi possível mover a demanda', 'error');
      carregar();
    }
  }

  async function salvar() {
    if (!edit.titulo.trim()) return Swal.fire('Atenção', 'O título não pode ficar vazio', 'warning');
    setSalvando(true);
    try {
      const corpo = {
        ...edit, prazo: edit.prazo || null, data_inicio: edit.data_inicio || null, data_fim: edit.data_fim || null,
        campanha_id: edit.campanha_id || null, tipo: edit.tipo || null,
        acoes: (edit.acoes || []).map(a => ({ id: a.id, acao: a.acao, titulo: a.titulo })),
      };
      if (edit.id) {
        await axios.put(`${API}/demandas/${edit.id}`, corpo);
        setEdit(null);
        await carregar();
      } else {
        const r = await axios.post(`${API}/demandas`, corpo);
        setEdit(null);
        const lista = await carregar();
        // criou com ações: reabre já salva, pra poder clicar em Gerar
        const nova = corpo.acoes.length && lista?.find(x => x.id === r.data.id);
        if (nova) abrir(nova);
      }
    } catch (err) {
      Swal.fire('Erro', err.response?.data?.error || 'Não foi possível salvar a demanda', 'error');
    } finally { setSalvando(false); }
  }

  async function excluir() {
    const ok = await Swal.fire({
      title: 'Excluir demanda?', text: edit.titulo, icon: 'warning', showCancelButton: true,
      confirmButtonText: 'Excluir', cancelButtonText: 'Cancelar', confirmButtonColor: '#b23b3b',
    });
    if (!ok.isConfirmed) return;
    try {
      await axios.delete(`${API}/demandas/${edit.id}`);
      setEdit(null);
      await carregar();
    } catch (err) {
      Swal.fire('Erro', err.response?.data?.error || 'Não foi possível excluir a demanda', 'error');
    }
  }

  const abrir = (d) => setEdit({
    ...VAZIA, ...d,
    descricao: d.descricao || '', tipo: d.tipo || '', prazo: d.prazo || '', data_inicio: d.data_inicio || '', data_fim: d.data_fim || '',
    campanha_id: d.campanha_id || '', solicitante: d.solicitante || '', link: d.link || '', filiais: d.filiais || [], acoes: d.acoes || [],
  });

  // Depois de gerar uma ação: recarrega e atualiza só as ações/campanha no modal (o resto que a pessoa digitou fica)
  async function aposGerar() {
    const lista = await carregar();
    axios.get(`${API}/campanhas`).then(r => setCampanhas(r.data)).catch(() => {});
    const nova = lista?.find(x => x.id === edit?.id);
    if (nova) setEdit(p => ({ ...p, acoes: nova.acoes, campanha_id: nova.campanha_id || p.campanha_id || '' }));
  }

  function abrirPorId(id) {
    const d = demandas.find(x => x.id === id);
    if (d) abrir(d);
    else Swal.fire('Demanda', 'Essa demanda não está na lista carregada (pode ter sido concluída há mais de 30 dias). Ligue "Mostrar todo o histórico" pra vê-la.', 'info');
  }

  // Nova demanda a partir de um dia do calendário: futuro entra como planejada, hoje/passado vai pro quadro
  const novaNoDia = (iso) => setEdit({ ...VAZIA, data_inicio: iso, status: iso > hoje() ? 'planejada' : 'a_fazer' });

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <h4 className="fw-bold mb-0"><i className="bi bi-kanban me-2"></i>Demandas</h4>
          <div className="btn-group btn-group-sm">
            {[['quadro', 'bi-kanban', 'Quadro'], ['calendario', 'bi-calendar3', 'Calendário']].map(([v, ic, lb]) => (
              <button key={v} className="btn" onClick={() => setAba(v)}
                style={aba === v ? { background: ROXO, color: '#fff', borderColor: ROXO } : { border: `1px solid ${ROXO}`, color: ROXO }}>
                <i className={`bi ${ic} me-1`} />{lb}
              </button>
            ))}
          </div>
        </div>
        <div className="d-flex align-items-center gap-2 small text-muted">
          {porColuna.a_fazer.length + porColuna.fazendo.length} em aberto
          {atrasadas > 0 && <span className="badge" style={{ background: '#fdeeee', color: '#b23b3b' }}><i className="bi bi-exclamation-triangle-fill me-1" />{atrasadas} atrasada{atrasadas > 1 ? 's' : ''}</span>}
          {planejadas > 0 && aba === 'quadro' && (
            <Tooltip texto="Demandas planejadas ficam só no calendário até alguém puxar pro quadro">
              <button className="btn btn-link btn-sm p-0" style={{ color: ROXO }} onClick={() => setAba('calendario')}><i className="bi bi-calendar3 me-1" />{planejadas} planejada{planejadas > 1 ? 's' : ''}</button>
            </Tooltip>
          )}
          <button className="btn btn-sm btn-outline-mkt" onClick={() => setEdit({ ...VAZIA })}><i className="bi bi-plus-lg me-1" />Nova (detalhada)</button>
        </div>
      </div>

      {/* Criação rápida: digita e Enter */}
      <form onSubmit={criarRapida} className="card border-0 shadow-sm mb-3">
        <div className="card-body py-2 d-flex flex-wrap gap-2 align-items-center">
          <i className="bi bi-lightning-charge-fill" style={{ color: ROXO }} />
          <input className="form-control flex-grow-1" style={{ minWidth: 220, flexBasis: 300 }} placeholder="Nova demanda — ex: Arte vaga motorista MU  (Enter pra adicionar)"
            value={rapida.titulo} onChange={e => setRapida(r => ({ ...r, titulo: e.target.value }))} maxLength={200} />
          <Tooltip texto="Prazo (opcional)">
            <input type="date" className="form-control" style={{ width: 160 }} value={rapida.prazo} onChange={e => setRapida(r => ({ ...r, prazo: e.target.value }))} />
          </Tooltip>
          <button type="submit" className="btn btn-primary-mkt" disabled={criando || !rapida.titulo.trim()}>
            {criando ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-plus-lg me-1" />Adicionar</>}
          </button>
        </div>
      </form>

      {/* Filtros */}
      <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
        <input className="form-control form-control-sm" style={{ maxWidth: 240 }} placeholder="Buscar…" value={busca} onChange={e => setBusca(e.target.value)} />
        <select className="form-select form-select-sm" style={{ maxWidth: 210 }} value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
          <option value="">Todos os tipos</option>
          {TIPOS.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
        </select>
        <select className="form-select form-select-sm" style={{ maxWidth: 190 }} value={filtroLoja} onChange={e => setFiltroLoja(e.target.value)}>
          <option value="">Todas as lojas</option>
          {filiais.map(f => <option key={f.codigo} value={f.codigo}>{f.nome}</option>)}
        </select>
        {aba === 'quadro' && <div className="form-check form-switch ms-1 mb-0 small">
          <input className="form-check-input" type="checkbox" id="dem-todas" checked={todasFeitas} onChange={e => setTodasFeitas(e.target.checked)} style={todasFeitas ? { background: ROXO, borderColor: ROXO } : {}} />
          <label className="form-check-label" htmlFor="dem-todas">Mostrar todo o histórico de feitas <span className="text-muted">(padrão: últimos 30 dias)</span></label>
        </div>}
      </div>

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border" style={{ color: ROXO }} /></div>
      ) : aba === 'calendario' ? (
        <CalendarioDemandas demandas={visiveis} onAbrir={abrir} onNova={novaNoDia} onMover={mover} />
      ) : (
        <div className="row g-3">
          {COLUNAS.map(col => (
            <div key={col.status} className="col-12 col-lg-4">
              <div
                className="rounded-3 p-2 h-100"
                style={{ background: sobreColuna === col.status ? col.bg : '#f6f4f5', minHeight: 200, transition: 'background .15s', outline: sobreColuna === col.status ? `2px dashed ${col.cor}` : 'none' }}
                onDragOver={e => { e.preventDefault(); setSobreColuna(col.status); }}
                onDragLeave={() => setSobreColuna(null)}
                onDrop={e => { e.preventDefault(); setSobreColuna(null); if (arrastando) mover(arrastando, col.status); setArrastando(null); }}
              >
                <div className="d-flex justify-content-between align-items-center px-1 mb-2">
                  <span className="fw-bold small" style={{ color: col.cor }}><i className={`bi ${col.icon} me-1`} />{col.label}</span>
                  <span className="badge rounded-pill" style={{ background: col.bg, color: col.cor }}>{porColuna[col.status].length}</span>
                </div>
                {porColuna[col.status].length === 0 && <div className="text-center text-muted small py-4">Nada aqui</div>}
                {porColuna[col.status].map(d => (
                  <CardDemanda key={d.id} d={d} nomeFilial={nomeFilial}
                    onAbrir={() => abrir(d)} onMover={mover}
                    onDragStart={() => setArrastando(d)} onDragEnd={() => { setArrastando(null); setSobreColuna(null); }} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {edit && (
        <ModalDemanda edit={edit} setEdit={setEdit} filiais={filiais} campanhas={campanhas}
          salvando={salvando} onSalvar={salvar} onExcluir={excluir} onFechar={() => setEdit(null)}
          onGerado={aposGerar} onAbrirDemanda={abrirPorId} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function CardDemanda({ d, nomeFilial, onAbrir, onMover, onDragStart, onDragEnd }) {
  const prazo = situacaoPrazo(d);
  const tipo = TIPO[d.tipo];
  const idx = ORDEM_STATUS.indexOf(d.status === 'cancelada' ? 'feito' : d.status);
  const cancelada = d.status === 'cancelada';
  return (
    <div className="card border-0 shadow-sm mb-2" draggable onDragStart={onDragStart} onDragEnd={onDragEnd}
      style={{ cursor: 'grab', opacity: cancelada ? 0.6 : 1, borderLeft: prazo?.label.startsWith('Atrasada') ? '3px solid #b23b3b' : '3px solid transparent' }}>
      <div className="card-body p-2" onClick={onAbrir} role="button">
        <div className="small fw-semibold mb-1" style={{ textDecoration: cancelada ? 'line-through' : 'none', wordBreak: 'break-word' }}>{d.titulo}</div>
        <div className="d-flex flex-wrap gap-1 align-items-center" style={{ fontSize: 11 }}>
          {prazo && <span className="badge" style={{ background: prazo.bg, color: prazo.cor }}><i className="bi bi-calendar-event me-1" />{prazo.label}</span>}
          {tipo && <span className="badge" style={{ background: '#F5EBF2', color: ROXO }}><i className={`bi ${tipo.icon} me-1`} />{tipo.label}</span>}
          {d.filiais.map(f => <span key={f} className="badge bg-light text-dark border">{nomeFilial(f)}</span>)}
          {d.data_inicio && <span className="badge bg-light text-dark border"><i className="bi bi-calendar-range me-1" />{fmtPeriodo(d.data_inicio, d.data_fim)}</span>}
          {d.campanha_nome && <span className="badge bg-light text-muted border"><i className="bi bi-megaphone me-1" />{d.campanha_nome}</span>}
          {d.acoes?.length > 0 && <span className="badge bg-light text-muted border" title="Ações geradas / previstas"><i className="bi bi-lightning me-1" />{d.acoes.filter(a => a.ref_id).length}/{d.acoes.length}</span>}
          {d.pai_titulo && <span className="text-muted text-truncate" style={{ maxWidth: 160 }} title={`Gerada de: ${d.pai_titulo}`}><i className="bi bi-diagram-2 me-1" />{d.pai_titulo}</span>}
          {d.solicitante && <span className="text-muted"><i className="bi bi-person me-1" />{d.solicitante}</span>}
          {d.link && <i className="bi bi-link-45deg text-muted" />}
          {d.descricao && <i className="bi bi-text-left text-muted" />}
          {cancelada && <span className="badge bg-secondary">Cancelada</span>}
        </div>
      </div>
      {/* setas: no celular não tem arrastar */}
      <div className="d-flex justify-content-between px-2 pb-1">
        <button className="btn btn-link btn-sm p-0 text-muted" disabled={idx <= 0} onClick={() => onMover(d, ORDEM_STATUS[idx - 1])} title="Voltar etapa"><i className="bi bi-chevron-left" /></button>
        <button className="btn btn-link btn-sm p-0 text-muted" disabled={idx >= ORDEM_STATUS.length - 1} onClick={() => onMover(d, ORDEM_STATUS[idx + 1])} title="Avançar etapa"><i className="bi bi-chevron-right" /></button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function ModalDemanda({ edit, setEdit, filiais, campanhas, salvando, onSalvar, onExcluir, onFechar, onGerado, onAbrirDemanda }) {
  const set = (campo) => (e) => setEdit(p => ({ ...p, [campo]: e.target.value }));
  const toggleFilial = (cod) => setEdit(p => {
    const c = parseInt(cod);
    return { ...p, filiais: p.filiais.includes(c) ? p.filiais.filter(x => x !== c) : [...p.filiais, c] };
  });
  // campanhas ativas/rascunho primeiro; encerradas só se já estiver vinculada
  const opcoesCampanha = campanhas.filter(c => c.status !== 'encerrada' || String(c.id) === String(edit.campanha_id));

  return (
    <div onClick={onFechar} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1050, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 16, overflowY: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3 p-4 mt-4" style={{ maxWidth: 640, width: '100%' }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0" style={{ color: ROXO }}>{edit.id ? 'Demanda' : 'Nova demanda'}</h5>
          <button className="btn-close" onClick={onFechar} />
        </div>

        <div className="mb-3">
          <label className="form-label small fw-bold">Título *</label>
          <input className="form-control" value={edit.titulo} onChange={set('titulo')} maxLength={200} placeholder="Ex: Arte vaga motorista Manhumirim" autoFocus />
        </div>

        <div className="row g-3 mb-3">
          <div className="col-md-4">
            <label className="form-label small fw-bold">Etapa</label>
            <select className="form-select" value={edit.status} onChange={set('status')}>
              <option value="planejada">Planejada (só no calendário)</option>
              {COLUNAS.map(c => <option key={c.status} value={c.status}>{c.label}</option>)}
              <option value="cancelada">Cancelada</option>
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label small fw-bold">Prazo <span className="text-muted fw-normal">(opcional)</span></label>
            <input type="date" className="form-control" value={edit.prazo} onChange={set('prazo')} />
          </div>
          <div className="col-md-4">
            <label className="form-label small fw-bold">Tipo <span className="text-muted fw-normal">(opcional)</span></label>
            <select className="form-select" value={edit.tipo} onChange={set('tipo')}>
              <option value="">—</option>
              {TIPOS.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </div>
        </div>

        <div className="row g-3 mb-3">
          <div className="col-6 col-md-4">
            <label className="form-label small fw-bold">Período — início <span className="text-muted fw-normal">(opcional)</span></label>
            <input type="date" className="form-control" value={edit.data_inicio} onChange={set('data_inicio')} />
          </div>
          <div className="col-6 col-md-4">
            <label className="form-label small fw-bold">Fim</label>
            <input type="date" className="form-control" value={edit.data_fim} min={edit.data_inicio || undefined} onChange={set('data_fim')} />
          </div>
          <div className="col-md-4 small text-muted d-flex align-items-end pb-2">
            <span><i className="bi bi-info-circle me-1" />Quando acontece (ex: a campanha). Prazo é a data de entrega do Marketing.</span>
          </div>
        </div>

        <AcoesDemanda edit={edit} setEdit={setEdit} campanhas={campanhas} onGerado={onGerado} onAbrirDemanda={onAbrirDemanda} />

        <div className="mb-3">
          <label className="form-label small fw-bold">Lojas <span className="text-muted fw-normal">(opcional — para quais lojas é a demanda)</span></label>
          <div className="d-flex flex-wrap gap-1">
            {filiais.map(f => {
              const on = edit.filiais.includes(parseInt(f.codigo));
              return (
                <button key={f.codigo} type="button" onClick={() => toggleFilial(f.codigo)} className="btn btn-sm"
                  style={on ? { background: ROXO, color: '#fff' } : { border: '1px solid #ddd', color: '#555' }}>
                  {on && <i className="bi bi-check-lg me-1" />}{f.nome}
                </button>
              );
            })}
          </div>
        </div>

        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <label className="form-label small fw-bold">Campanha <span className="text-muted fw-normal">(opcional)</span></label>
            <select className="form-select" value={edit.campanha_id} onChange={set('campanha_id')}>
              <option value="">—</option>
              {opcoesCampanha.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
            </select>
          </div>
          <div className="col-md-6">
            <label className="form-label small fw-bold">Quem pediu <span className="text-muted fw-normal">(opcional)</span></label>
            <input className="form-control" value={edit.solicitante} onChange={set('solicitante')} maxLength={100} placeholder="Ex: Vander (gerente CR)" />
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label small fw-bold">Detalhes <span className="text-muted fw-normal">(opcional)</span></label>
          <textarea className="form-control" rows={4} value={edit.descricao} onChange={set('descricao')} maxLength={4000} placeholder="Ex: tamanho A3, usar a foto do produto 28424, prazo da gráfica…" />
        </div>

        <div className="mb-3">
          <label className="form-label small fw-bold">Link da arte / pasta <span className="text-muted fw-normal">(opcional)</span></label>
          <div className="input-group">
            <input className="form-control" value={edit.link} onChange={set('link')} maxLength={500} placeholder="Ex: https://drive.google.com/…" />
            {/^https?:\/\//.test(edit.link) && <a className="btn btn-outline-secondary" href={edit.link} target="_blank" rel="noreferrer" title="Abrir"><i className="bi bi-box-arrow-up-right" /></a>}
            {/^\/[a-z]/.test(edit.link) && <Link className="btn btn-outline-secondary" to={edit.link} title="Abrir a tela no sistema"><i className="bi bi-box-arrow-in-right" /></Link>}
          </div>
        </div>

        {edit.id && (
          <div className="small text-muted mb-3">
            Criada em {new Date(edit.criado_em).toLocaleString('pt-BR')}
            {edit.origem === 'notion' && <> · <i className="bi bi-journal-arrow-down" /> veio do Notion</>}
            {edit.demanda_pai_id && <> · <i className="bi bi-diagram-2" /> gerada de <button type="button" className="btn btn-link btn-sm p-0 align-baseline" style={{ color: ROXO }} onClick={() => onAbrirDemanda(edit.demanda_pai_id)}>{edit.pai_titulo || `#${edit.demanda_pai_id}`}</button></>}
            {edit.concluido_em && <> · concluída em {new Date(edit.concluido_em).toLocaleString('pt-BR')}</>}
          </div>
        )}

        <div className="d-flex justify-content-between gap-2">
          <div>{edit.id && <button className="btn btn-outline-danger" onClick={onExcluir}><i className="bi bi-trash me-1" />Excluir</button>}</div>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary" onClick={onFechar}>Cancelar</button>
            <button className="btn" style={{ background: ROXO, color: '#fff' }} disabled={salvando} onClick={onSalvar}>
              {salvando ? <><span className="spinner-border spinner-border-sm me-1" />Salvando…</> : <><i className="bi bi-save me-1" />Salvar</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
